import React, { useState, useEffect } from "react";
import "../styles/QnaModal.css";
import { useMutation } from "@tanstack/react-query";
import { useLoggedInUserId } from "../../api/GetLoggedInUserId";
import { toast } from "../../utils/toast";

const QnaUpdateModal = ({ isOpen, onClose, itemId, onQnaSubmitted, qnaId }) => {
    // ✅ 상태 관리 (문의 제목, 내용, 공개 여부)
    const [isLoading, setIsLoading] = useState(true);
    const [title, setTitle] = useState("");
    const [itemName, setItemName] = useState("");
    const [content, setContent] = useState("");
    const [visibility, setVisibility] = useState("PUBLIC");

    const { data: memberId } = useLoggedInUserId(); // ✅ 로그인된 사용자 ID 가져오기

    // ✅ 모달이 열릴 때 데이터를 불러오기
    useEffect(() => {
        if (!isOpen || !qnaId) return; // ❌ 모달이 닫혀있거나 qnaId가 없으면 실행 X

        const fetchQnaData = async () => {
            try {
                const response = await fetch(`/api/items/qna/update?qnaId=${qnaId}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    }
                });

                if (!response.ok) {
                    throw new Error("상품 문의 데이터 가져오기 실패");
                }

                const data = await response.json();
                console.log("불러온 QNA 데이터:", data);

                // ✅ 기존 문의 데이터를 상태에 반영
                setTitle(data.qnaForm.title || "");
                setItemName(data.qnaForm.itemName || "");
                setContent(data.qnaForm.content || "");
                setVisibility(data.qnaForm.visibility || "PUBLIC");
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchQnaData();
    }, [isOpen, qnaId]); // ✅ isOpen과 qnaId가 변경될 때만 실행

    // ✅ 모달이 닫힐 때 입력값 초기화
    useEffect(() => {
        if (!isOpen) {
            setItemName("");
            setTitle("");
            setContent("");
            setVisibility("PUBLIC");
        }
    }, [isOpen]);

    // ✅ 상품 문의 수정 Mutation
    const updateQnaMutation = useMutation({
        mutationFn: async () => {
            const qnaData = {
                qnaId: qnaId,  // ✅ request body에 포함
                itemId: itemId,
                title: title,
                content: content,
                visibility: visibility
            };

            const response = await fetch(`/api/items/qna/update?qnaId=${qnaId}`, { // ✅ 쿼리 스트링으로 qnaId 추가
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(qnaData),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.result || "문의 수정 실패");
            }
            return data;
        },
        onSuccess: () => {
            onQnaSubmitted(); // ✅ 부모 컴포넌트로 문의 등록 완료 신호 전달
            toast("✅ 문의가 성공적으로 등록되었습니다.");
            window.location.reload(); // ✅ 페이지 새로고침
            onClose();
        },
        onError: (error) => {
            toast(`❌ 문의 등록 실패: ${error.message}`);
        }
    });

    // ✅ 문의 등록 핸들러
    const handleSubmit = () => {
        if (title.length < 4 || content.length < 10) {
            toast("제목은 4자 이상, 내용은 10자 이상 입력해주세요.");
            return;
        }

        updateQnaMutation.mutate({
            title,
            content,
            visibility,
        });
    };

    if (!isOpen) return null;

    if (isLoading) {
        return (
            <div className="sc-modal-backdrop">
                <div className="sc-modal sc-modal--form">
                    <h2>Q&A 문의하기</h2>
                    <p>데이터를 불러오는 중입니다...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="sc-modal-backdrop" onClick={onClose}>
            <div className="sc-modal sc-modal--form" onClick={(e) => e.stopPropagation()}>
                {/* 상단 타이틀 */}
                <div className="modal-header">
                    <h2>Q&A 문의하기</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className={"qna-modal-contents"}>
                    {/* 상품명 */}
                    <div className="qna-modal-item-name">상품명 : {itemName}</div>

                    {/* 공개/비공개 선택 */}
                    <div className="visibility-options">
                        {[["PRIVATE", "비밀글"], ["PUBLIC", "전체공개"]].map(([value, label]) => (
                            <label className="sc-choice" key={value}>
                                <input
                                    type="radio"
                                    className="sc-radio"
                                    name="visibility"
                                    value={value}
                                    checked={visibility === value}
                                    onChange={() => setVisibility(value)}
                                />
                                {label}
                            </label>
                        ))}
                    </div>

                    {/* 입력 폼 */}
                    <input
                        type="text"
                        placeholder="제목을 입력해주세요"
                        className="modal-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <textarea
                        placeholder="문의하실 내용을 작성하세요 (최소 10자 이상)"
                        className="modal-textarea"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                    ></textarea>

                    {/* 유의사항 */}
                    <div className="qna-warning">
                        <h3 className="qna-warning-title">* Q&A 작성시 유의사항 *</h3>
                        <p>부적절하거나 게시글 운영 방식과 맞지 않는 글은 무통보 삭제됩니다.</p>
                        <p>개인정보를 포함한 직거래 유도 내용</p>
                        <p>비방/욕설/명예훼손에 해당되는 게시물</p>
                        <p>소프트캣과 관계없는 타사이트와 가격비교 및 구매문의</p>
                        <p>게시판 운영 방식과 맞지 않는 게시물(홍보, 광고, 음란성 글 등)</p>
                        <p>정치, 종교 같은 논쟁을 일으킬 수 있는 게시물.</p>
                    </div>

                    {/* 버튼 */}
                    <div className="modal-buttons">
                        <button className="submit-btn" onClick={handleSubmit} disabled={updateQnaMutation.isLoading}>
                            {updateQnaMutation.isLoading ? "수정 중..." : "수정하기"}
                        </button>
                        <button className="cancel-btn" onClick={onClose}>취소</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QnaUpdateModal;
